import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BoardingCheckListService, MenuPinService, SettingsService } from '../../services';
import { CustomerService } from '../../services';
import { FileStorageService } from '../../services';
import { LoggerService } from '../../services';
import { MenuService } from '../../services';
import { SecurityService } from '../../services';
import { SystemDataService } from '../../services';
import { TaggedLogger } from '../../services/taggedLogger';
import { TermsConditionsService } from '../../services';
import { UserService } from '../../services';
import { AppComponent } from './app.component';
import { customerMenu } from './menus/customer-menu';
import { defaultMenu } from './menus/default-menu';
import { engineiusMenu } from './menus/engineius-menu';
import { saasCustomerMenu } from './menus/saas-customer-menu';
import { supplierMenu } from './menus/supplier-menu';
import { RouteRecommendationService } from '../../services/route-recommendation.service';
import { SecurableMenuItem } from './securable-menu.item';
import { ISystemSettings } from '../../interfaces/system-settings';
import { FlatMenuItem } from '../../services/menu-pin.service';

@Component({
  selector: 'app-menu',
  templateUrl: './app-menu.component.html',
  styleUrls: ['./app-menu.component.scss'],
})
export class AppMenuComponent implements OnInit, OnDestroy {
  @Input() reset: boolean;

  private logger: TaggedLogger;

  model: SecurableMenuItem[];
  private orgId: number = 0;
  public boardingCheckList: any = {};

  bbwtVersion: string;
  bbwtVersion2: string;
  date: Date = new Date();
  logo: string;
  orgTypeId: number;
  userName: string;
  organisationRouterLink: string;
  organisation;

  systemSettings: ISystemSettings;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  searchTerm = '';
  searchResults: FlatMenuItem[] = [];
  activeResultIndex = 0;
  private flatItems: FlatMenuItem[] = [];

  /**
   * @constructor
   */
  constructor(
    @Inject(forwardRef(() => AppComponent)) public app: AppComponent,
    public http: HttpClient,
    public termsService: TermsConditionsService,
    private securityService: SecurityService,
    private userService: UserService,
    private router: Router,
    private boardingCheckListService: BoardingCheckListService,
    private menuService: MenuService,
    private systemService: SystemDataService,
    private systemSettingsService: SettingsService,
    private customerService: CustomerService,
    private fileStorageService: FileStorageService,
    private routeRecommendationService: RouteRecommendationService,
    public menuPinService: MenuPinService,
    private ngZone: NgZone,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.taggedLogger('Menu');
  }

  /**
   * ngOnInit: Sets up the menu and listens for menu reload events?
   */
  async ngOnInit() {
    // Outside the zone so every keystroke in the app doesn't trigger change detection
    this.ngZone.runOutsideAngular(() =>
      document.addEventListener('keydown', this.onDocumentKeydown),
    );

    this.menuService.reloadMenu.subscribe((value: boolean) => {
      if (value !== null) this.menuVisible(value);
    });

    this.customerService.getOrganisationNameAndLogo().then((org) => {
      this.logo = org.logo;
    });

    this.systemService.getVersionInfo().then((version) => {
      if (version) {
        this.bbwtVersion = version.productVersion;
        this.bbwtVersion2 = version.pipeline;
      }
    });

    this.systemSettingsService.GetSystemSettings().then((data) => {
      this.systemSettings = data;
    });

    this.userService.getLoggedUser().then((res) => {
      this.userName = `${res.firstName} ${res.lastName}`;
    });

    this.organisation = await this.userService.getCurrentUserOrg();

    this.orgId = this.organisation.id;
    this.organisationRouterLink = '/app/organisation/' + this.orgId;
    this.orgTypeId = this.organisation.organisationTypeId;

    const menuItems = await this.getMenuItems(this.organisation);

    this.model = await this.applySecurity(<SecurableMenuItem[]>menuItems);
    this.refreshMenu();

    if (this.orgTypeId === 1) {
      this.menuPinService
        .load()
        .catch((error) => this.logger.error('Error loading menu pins', error));
    }

    // You cannot add "url" items before the this.model has been created, so adding them after
    //if (res.organisationTypeId == 3) {

    //    this.model.push({ label: "Driver Academy", url: 'https://www.engineius.co.uk/driver-academy' });

    //    console.log(this.model);
    //}
  }

  /**
   * Adds menu items to the menu
   * @param res The response from this.userService.getCurrentUserOrg()
   * @private
   */
  private async getMenuItems(res): Promise<SecurableMenuItem[]> {
    this.logger.trace('getMenuItems', res);

    if (res.organisationTypeId === 1) {
      this.logger.debug('Is Engineius Menu');
      return engineiusMenu;
    }

    if (res.organisationTypeId === 2) {
      this.logger.debug('Is Customer Menu');
      return customerMenu(this.organisationRouterLink);
    }

    if (res.organisationTypeId === 3) {
      this.logger.debug('Is Supplier Menu');
      const defaultToRouteSearch = await this.routeRecommendationService.isDefaultToRouteSearch();
      return supplierMenu(this.organisationRouterLink, defaultToRouteSearch);
    }

    if (res.organisationTypeId === 5) {
      this.logger.debug('Is SaaS Customer Menu');
      return saasCustomerMenu();
    }

    return defaultMenu;
  }

  /**
   * todo: check the reuse of apply security here, seems off.
   * @param visible
   * @private
   */
  private async menuVisible(visible: boolean) {
    if (visible) {
      this.model = await this.applySecurity(this.model);
    } else {
      for (let menuItem of this.model) {
        if (menuItem.label === 'Home') continue; // Never hide the home button?

        menuItem.visible = false;
      }
    }

    this.refreshMenu(!visible);
  }

  search() {
    const terms = this.searchTerm
      .toLowerCase()
      .split(/\s+/)
      .filter((term) => !!term);

    this.activeResultIndex = 0;
    this.searchResults = terms.length
      ? this.flatItems.filter((item) => {
          const text = `${item.path} ${item.label}`.toLowerCase();
          return terms.every((term) => text.includes(term));
        })
      : [];
  }

  clearSearch() {
    this.searchTerm = '';
    this.search();
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeResultIndex = Math.min(this.activeResultIndex + 1, this.searchResults.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
    } else if (event.key === 'Enter') {
      const result = this.searchResults[this.activeResultIndex];
      if (!result) return;

      this.router.navigateByUrl(result.routerLink);
      this.onNavigate(result);
      this.searchInput.nativeElement.blur();
    } else if (event.key === 'Escape') {
      this.clearSearch();
      this.searchInput.nativeElement.blur();
    }
  }

  onNavigate(item: FlatMenuItem) {
    this.menuService.setselectedMenu(item.label);
    this.clearSearch();

    this.app.overlayMenuActive = false;
    this.app.staticMenuMobileActive = false;
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  private refreshMenu(menuHidden = false) {
    this.flatItems = this.flatten(this.model);
    this.menuPinService.setAvailableItems(this.flatItems, menuHidden);
    this.search();
  }

  private onDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key !== '/') return;

    // A collapsed sidebar is moved off-screen rather than hidden, so check it's actually in view
    const input = this.searchInput?.nativeElement;
    const rect = input?.getBoundingClientRect();
    if (!rect || rect.right <= 0 || rect.left >= window.innerWidth) return;

    const target = event.target as HTMLElement;
    if (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      return;

    event.preventDefault();
    input.focus();
  };

  private flatten(items: SecurableMenuItem[], path: string[] = []): FlatMenuItem[] {
    const flat: FlatMenuItem[] = [];

    for (const item of items ?? []) {
      if (item.visible === false) continue;

      if (item.items) {
        flat.push(...this.flatten(<SecurableMenuItem[]>item.items, [...path, item.label.trim()]));
      } else if (item.routerLink) {
        flat.push({ label: item.label, path: path.join(' › '), routerLink: item.routerLink[0] });
      }
    }

    return flat;
  }

  private async applySecurity(menuItems: SecurableMenuItem[]): Promise<SecurableMenuItem[]> {
    const secured: SecurableMenuItem[] = [];
    this.logger.trace('applySecurity()', menuItems);

    if (!menuItems || !menuItems.length) return secured;

    for (let menuItem of menuItems) {
      if (menuItem.url) {
        secured.push({
          ...menuItem,
          visible: true,
        });
        continue;
      }

      if (menuItem.items) {
        const childItems = await this.applySecurity(<SecurableMenuItem[]>menuItem.items);
        const visible = !!childItems.find((item) => item.visible);
        menuItem.visible = !!(menuItem.items as SecurableMenuItem[]).find((item) => item.visible);

        const securedChildren = {
          ...menuItem,
          items: childItems,
          visible,
        };
        this.logger.trace('With Child Items:', visible, securedChildren);
        secured.push(securedChildren);
        continue;
      }

      if (menuItem.routerLink) {
        const visible = await this.securityService.pageIsAvailable(
          menuItem.routerLink,
          menuItem.securityHandler,
        );

        secured.push({
          ...menuItem,
          visible,
        });
      }
    }

    return secured;
  }
}
